import type { FaceitPlayerRef } from "@69ly/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { errorText } from "../lib/errors.ts";
import { useLinkFaceitTeam, useRefreshTeamFaceit, useTeamFaceit } from "../lib/faceit-queries.ts";
import { Avatar } from "./Avatar.tsx";
import { Icon } from "./Icon.tsx";
import { Select } from "./Select.tsx";

/** FACEIT's skill level, in its colour band (1, 2–3, 4–7, 8–9, 10). */
export function FaceitLevel({ level }: { level: number | null }) {
  const { t } = useTranslation();
  if (!level) return null;
  const band = level >= 10 ? 5 : level >= 8 ? 4 : level >= 4 ? 3 : level >= 2 ? 2 : 1;
  return (
    <span className={`faceit-level is-${band}`} title={t("faceit.level", { n: level })}>
      {level}
    </span>
  );
}

export function FaceitPlayer({ player }: { player: FaceitPlayerRef }) {
  return (
    <a className="faceit-player" href={player.url} target="_blank" rel="noreferrer">
      <FaceitLevel level={player.level} />
      <span>{player.nickname}</span>
      <Icon name="external" size={12} />
    </a>
  );
}

/** The Team page's FACEIT card: each member's FACEIT profile, and the team's FACEIT team. */
export function TeamFaceitCard({ teamId }: { teamId: string }) {
  const { t } = useTranslation();
  const faceit = useTeamFaceit(teamId);
  const link = useLinkFaceitTeam(teamId);
  const refresh = useRefreshTeamFaceit(teamId);
  const data = faceit.data;

  return (
    <section className="card faceit-card" aria-labelledby="faceit-title">
      <div className="faceit-card-head">
        <h2 id="faceit-title" className="eyebrow">
          {t("faceit.title")}
        </h2>
        {data?.configured && (
          <button type="button" className="btn btn-quiet btn-small" disabled={refresh.isPending} onClick={() => refresh.mutate()} title={t("faceit.refreshHelp")}>
            <Icon name="loop" size={14} />
            {refresh.isPending ? t("faceit.refreshing") : t("faceit.refresh")}
          </button>
        )}
      </div>
      {faceit.isPending ? (
        <p className="muted small">{t("common.loading")}</p>
      ) : faceit.error || !data ? (
        <p className="field-error small">{errorText(t, faceit.error)}</p>
      ) : !data.configured ? (
        <p className="muted small">{t("faceit.off")}</p>
      ) : (
        <>
          <div className="faceit-team">
            {data.team && !data.canEdit ? (
              <a className="faceit-team-name" href={data.team.url} target="_blank" rel="noreferrer">
                <Avatar name={data.team.name} url={data.team.avatar} size={28} />
                <b>{data.team.name}</b>
                <Icon name="external" size={12} />
              </a>
            ) : !data.team ? (
              <p className="muted small">{data.canEdit ? t("faceit.linkHelp") : t("faceit.notLinked")}</p>
            ) : null}
            {data.canEdit && (
              <div className="faceit-link-row">
                <Select
                  label={t("faceit.teamLabel")}
                  value={data.team?.id ?? null}
                  placeholder={data.options.length ? t("faceit.pickTeam") : t("faceit.noTeams")}
                  groups={[{ options: data.options.map((o) => ({ value: o.id, label: o.name, icon: <Avatar name={o.name} url={o.avatar} size={18} /> })) }]}
                  onChange={(v) => link.mutate(v)}
                />
                {data.team && (
                  <>
                    <a className="btn btn-quiet btn-small" href={data.team.url} target="_blank" rel="noreferrer" aria-label={t("faceit.teamPage", { name: data.team.name })}>
                      <Icon name="external" size={14} />
                    </a>
                    <button type="button" className="btn btn-quiet btn-small" disabled={link.isPending} onClick={() => link.mutate(null)}>
                      {t("faceit.unlink")}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <ul className="faceit-members">
            {data.members.map((m) => (
              <li key={m.user.id}>
                <span className="player-cell">
                  <Avatar name={m.user.name} url={m.user.avatarUrl} size={24} />
                  <span className="faceit-member-name">{m.user.name}</span>
                </span>
                {m.player ? <FaceitPlayer player={m.player} /> : <span className="muted small">{m.checkedAt ? t("faceit.noAccount") : t("faceit.notChecked")}</span>}
              </li>
            ))}
          </ul>
          {data.error && <p className="field-error small">{data.error}</p>}
          {link.error && <p className="field-error small">{errorText(t, link.error)}</p>}
          <Link to="/matches?view=faceit" className="btn btn-secondary btn-small faceit-card-more">
            {t("faceit.seeMatches")}
          </Link>
        </>
      )}
    </section>
  );
}
